'use client';

import { faAngleLeft, faLocationDot, faPrint } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { useState, useRef, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';

import { Button, Dropdown, MatchupStatusBadge } from '@/components/atoms';
import { WithdrawBadge } from '@/components/atoms/WithdrawBadge';
import { MATCH_ACTIONS, MATCHUP_MODAL_TYPE } from '@/components/constants/constants';
import { GameDayStatusModal } from '@/components/forms/modals/GameDayStatusModal';
import { DreamBreakerBox } from '@/components/molecules';
import { SimplifiedScoreSheetPrint, PrintMatchesModal, OfficialScoreSheetPrint, MlpScoreSheetPrint } from '@/components/molecules/prints';
import {
	getMatchupStatusApiValue,
	MatchCompletedEnum,
	MatchStatusApiMap,
	MatchStatusEnum,
	MatchupCompletedEnum,
	MatchupCompletedEnumTypes,
	MatchupStatusEnum,
	TEAM_LEAGUES_MODULE
} from '@/domain/constants/constants';
import { Matches } from '@/entities';
import { useUpdateMatchup } from '@/hooks/matches/useUpdateMatchup';
import { useSystemLocationCourtsQuery } from '@/hooks/system-locations/useSystemLocationCourtsQuery';
import { useTieBreakerMatch } from '@/hooks/tie-breaker/useTieBreakerMatch';
import { useRotationsInfoQuery } from '@/hooks/tie-breaker/useTieBreakerRotationsInfo';
import { useTiebreakerStore } from '@/store/tie-breaker-store';
import { DateUtil } from '@/utils/date';
import { PodsResponse } from '@/utils/types';

interface MatchLineupHeaderProps {
	matchupData: Matches;
	backLinkUrl: string;
	previousPageTitle: string;
	disableActionsDropdown?: boolean;
	isTieBreaker?: boolean;
	podsResponse?: PodsResponse;
}

export const MatchLineupHeader = ({
	matchupData,
	backLinkUrl,
	previousPageTitle,
	disableActionsDropdown = false,
	isTieBreaker = false,
	podsResponse
}: MatchLineupHeaderProps) => {
	const [activeModal, setActiveModal] = useState('');
	const [teamName, setTeamName] = useState('');
	const [selectedDropdownOption, setSelectedDropdownOption] = useState(MatchupCompletedEnumTypes.PLAYED_MATCHUP_COMPLETION_TYPE);
	const [prevMatchupStatus, setPrevMatchupStatus] = useState(matchupData.matchupStatus);
	const [dropdownWidth, setDropdownWidth] = useState<number>(0);
	const [showPrintModal, setShowPrintModal] = useState(false);
	const [selectedPrintOptions, setSelectedPrintOptions] = useState<{
		sheetType: 'official' | 'simplified' | 'mlp';
		sheetSize: 'half' | 'full';
	}>({
		sheetType: 'official',
		sheetSize: 'half'
	});
	const mobileDropdownRef = useRef<HTMLDivElement>(null);
	const showPrintButton = !isTieBreaker;

	const { ianaTzIdentifier, abbreviation } = matchupData.timezone;
	const matchupStartDate = DateUtil.formatUTCInTimezone(matchupData.formattedStartDate, 'MMM D, YYYY | hh:mm A', ianaTzIdentifier);

	const { status: tieBreakerMatchStatus, match: tieBreakerMatch, tieBreakerTitle, matchCompletedType } = useTieBreakerMatch(matchupData);
	const { data: rotationsInfo } = useRotationsInfoQuery(tieBreakerMatch?.matchUuid ?? '');

	const isTieBreakerMatchCompleted =
		tieBreakerMatchStatus === MatchStatusApiMap[MatchStatusEnum.COMPLETED_MATCH_STATUS] &&
		matchCompletedType === MatchCompletedEnum.PLAYED_MATCH_COMPLETED_TYPE;

	const scrollToTiebreaker = useTiebreakerStore((state) => state.scrollToTiebreaker);

	const { data: systemLocationCourtsResponse } = useSystemLocationCourtsQuery({
		moduleFor: TEAM_LEAGUES_MODULE.toString(),
		moduleUuid: matchupData.matches[0]?.moduleUuid ?? '',
		locationId: matchupData.locationUuid ?? ''
	});

	const { mutate: updateMatchupMutation, isPending: isUpdatingMatchup } = useUpdateMatchup();

	const contentRef = useRef<HTMLDivElement>(null);

	const generatePrintFilename = () => {
		const today = new Date();
		const dateStr = today.toLocaleDateString('en-CA').replace(/-/g, '');

		const eventTitle = matchupData.seasonTitle?.replace(/[^a-zA-Z0-9]/g, '_') || 'TeamLeague';
		const sheetType = selectedPrintOptions.sheetType.charAt(0).toUpperCase() + selectedPrintOptions.sheetType.slice(1);
		const sheetSize = selectedPrintOptions.sheetSize === 'half' ? 'HalfSheet' : 'FullSheet';

		return `${eventTitle}_${sheetType}_${sheetSize}_${dateStr}.pdf`;
	};

	const reactToPrintFn = useReactToPrint({
		contentRef,
		documentTitle: generatePrintFilename()
	});

	useEffect(() => {
		const updateWidth = () => {
			if (mobileDropdownRef.current) {
				setDropdownWidth(mobileDropdownRef.current.offsetWidth);
			}
		};

		// Initial width calculation
		updateWidth();

		// Update on resize
		window.addEventListener('resize', updateWidth);
		return () => window.removeEventListener('resize', updateWidth);
	}, []);

	const handleActionSelect = (value: string | number) => {
		if (value == 2) {
			setActiveModal(MATCHUP_MODAL_TYPE.WITHDRAW);
			setTeamName(matchupData.teamOneTitle);
			setSelectedDropdownOption(MatchupCompletedEnumTypes.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE);
		} else if (value == 3) {
			setActiveModal(MATCHUP_MODAL_TYPE.WITHDRAW);
			setTeamName(matchupData.teamTwoTitle);
			setSelectedDropdownOption(MatchupCompletedEnumTypes.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE);
		} else if (value == 4) {
			setActiveModal(MATCHUP_MODAL_TYPE.CANCEL);
			setTeamName('');
			setSelectedDropdownOption(MatchupCompletedEnumTypes.CANCELED_MATCHUP_COMPLETION_TYPE);
		} else {
			setActiveModal('');
			setTeamName('');
			setSelectedDropdownOption(MatchupCompletedEnumTypes.PLAYED_MATCHUP_COMPLETION_TYPE);
		}
	};

	const handleUndoAction = () => {
		let status = MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS;

		if (
			prevMatchupStatus === MatchupStatusEnum.UNSCHEDULED_MATCHUP_STATUS ||
			prevMatchupStatus === MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS ||
			prevMatchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS
		) {
			status = prevMatchupStatus;
		}

		updateMatchupMutation({
			matchup_uuid: matchupData.uuid,
			matchup_status: getMatchupStatusApiValue(status)
		});
	};

	const handlePrintModalOpen = () => {
		setShowPrintModal(true);
	};

	const handlePrintWithOptions = (data: { sheetType: 'official' | 'simplified' | 'mlp'; sheetSize: 'half' | 'full' }) => {
		setSelectedPrintOptions(data);

		// Trigger print after a short delay to allow component to render
		setTimeout(() => {
			reactToPrintFn();
		}, 100);
	};

	const isHomeWinner = matchupData.winner === 1;
	const isAwayWinner = matchupData.winner === 2;

	const isMatchupCancelledOrWithdrawn =
		(matchupData.matchupCompletedType === MatchupCompletedEnum.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE ||
			matchupData.matchupCompletedType === MatchupCompletedEnum.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE ||
			matchupData.matchupCompletedType === MatchupCompletedEnum.CANCELED_MATCHUP_COMPLETION_TYPE) &&
		matchupData.matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS;

	const showHomeTeamWithdrawBadge =
		matchupData.matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS &&
		matchupData.matchupCompletedType === MatchupCompletedEnum.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE;

	const showAwayTeamWithdrawBadge =
		matchupData.matchupStatus === MatchupStatusEnum.COMPLETED_MATCHUP_STATUS &&
		matchupData.matchupCompletedType === MatchupCompletedEnum.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE;

	const handleModalConfirm = () => {
		setPrevMatchupStatus(matchupData.matchupStatus);

		updateMatchupMutation({
			matchup_uuid: matchupData.uuid,
			matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.COMPLETED_MATCHUP_STATUS),
			matchup_completed_type: selectedDropdownOption
		});

		setActiveModal('');
	};

	const dreamBreakerProps = {
		courts: systemLocationCourtsResponse?.data ?? [],
		match: {
			matchUuid: tieBreakerMatch?.matchUuid ?? '',
			courtUuid: tieBreakerMatch?.courtUuid ?? '',
			matchAssignedToCourt: tieBreakerMatch?.matchAssignedToCourt ?? '',
			matchPlannedStart: tieBreakerMatch?.matchPlannedStart ?? ''
		},
		moduleUuid: matchupData.matches[0]?.moduleUuid ?? '',
		locationUuid: matchupData.locationUuid ?? ''
	};

	return (
		<>
			{activeModal ? (
				<GameDayStatusModal activeModal={activeModal} onClose={() => setActiveModal('')} onSubmit={handleModalConfirm} teamName={teamName} />
			) : null}

			<PrintMatchesModal open={showPrintModal} onOpenChange={setShowPrintModal} onPrint={handlePrintWithOptions} />

			<div className="mb-4 flex h-6 items-center gap-2 print-hidden">
				<Link href={backLinkUrl ?? '#'} prefetch={false} className="flex items-center gap-2 text-gray-900 hover:text-gray-700">
					<FontAwesomeIcon icon={faAngleLeft} className="size-5" />
					<span className="text-base font-medium leading-6">{previousPageTitle}</span>
				</Link>
			</div>

			{/* Header Section */}
			<div className="flex items-center justify-between pb-4 sm:pb-9 print-hidden">
				<h1 className="text-xl font-semibold text-gray-900 sm:text-2xl">{isTieBreaker ? 'Add Tie Breaker' : matchupData.seasonTitle}</h1>
				{showPrintButton ? (
					<Button
						className="hidden ml-2 rounded-lg border border-gray-300 bg-white font-semibold !text-gray-700 sm:flex hover:bg-gray-50"
						onClick={handlePrintModalOpen}
					>
						<FontAwesomeIcon icon={faPrint} className="mr-2" /> Print
					</Button>
				) : null}
				<Button
					className="flex size-10 items-center justify-center rounded-lg border border-gray-500 bg-white sm:hidden hover:bg-gray-50"
					onClick={handlePrintModalOpen}
				>
					<FontAwesomeIcon icon={faPrint} className="text-gray-700" />
				</Button>
			</div>

			{/* Mobile Actions Dropdown */}
			{!isMatchupCancelledOrWithdrawn && !disableActionsDropdown && (
				<div className="flex flex-col gap-2 mb-4 sm:hidden print-hidden">
					<div ref={mobileDropdownRef}>
						<Dropdown
							options={MATCH_ACTIONS.map((option) => ({
								value: option.value,
								label: option.label
							}))}
							onSelect={handleActionSelect}
							placeholder={'Withdraw'}
							value={'Withdraw'}
							showAll={false}
							menuListStyle={{ minWidth: `${dropdownWidth}px` }}
							className="hover:bg-gray-50"
						/>
					</div>
					{(matchupData.matchupStatus === MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS ||
						matchupData.matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS ||
						matchupData.matchupStatus === MatchupStatusEnum.READY_TO_BE_STARTED_STATUS) && (
						<DreamBreakerBox {...dreamBreakerProps} matchupData={matchupData} />
					)}

					{isTieBreakerMatchCompleted ? (
						<Button
							onClick={scrollToTiebreaker}
							className="float-right font-semibold text-sm bg-darkorange-400 mt-3 text-white rounded-md px-2 py-1"
						>
							Go to {tieBreakerTitle}
						</Button>
					) : null}
				</div>
			)}

			{isMatchupCancelledOrWithdrawn && !disableActionsDropdown ? (
				<Button
					className="sm:hidden !bg-orange-500 hover:!bg-orange-700 !text-white text-nowrap w-full mb-5"
					onClick={handleUndoAction}
					disabled={isUpdatingMatchup}
				>
					Undo
					{matchupData.matchupCompletedType === MatchupCompletedEnum.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE ||
					matchupData.matchupCompletedType === MatchupCompletedEnum.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE
						? ' withdraw'
						: ' cancel'}
				</Button>
			) : null}

			<div className="flex flex-col sm:flex-row sm:justify-between printable ">
				<div className="w-full">
					{/* Desktop Layout */}
					<div className="hidden sm:block">
						<div className="flex justify-between">
							<div className="min-w-80 border border-gray-[##D0D5DD] rounded-xl p-3 bg-white">
								<div className="flex items-center gap-20">
									<p className="text-sm font-medium text-gray-600">
										{matchupStartDate} {abbreviation}
									</p>

									{!isMatchupCancelledOrWithdrawn && <MatchupStatusBadge type={matchupData.matchupStatus} />}
								</div>
								{matchupData.venue ? (
									<div className="mt-2 flex items-center gap-2">
										<FontAwesomeIcon icon={faLocationDot} className="size-4 text-gray-500" />
										<p className="text-sm font-medium text-gray-600">{matchupData.venue}</p>
									</div>
								) : null}

								<div className="my-4 space-y-2">
									<div className="flex items-center justify-between">
										<p className="text-lg font-semibold text-blue-500">
											{matchupData.teamTwoTitle} <span className="text-lg font-medium text-gray-900">(A)</span>
										</p>

										<div className="flex flex-row gap-2">
											{showAwayTeamWithdrawBadge ? <WithdrawBadge /> : null}
											<span
												className={`text-lg font-medium min-w-6 text-center ${isAwayWinner ? 'text-success-600' : 'text-gray-900'}`}
											>
												{matchupData.teamTwoScore}
											</span>
										</div>
									</div>

									<div className="flex items-center justify-between">
										<p className="text-lg font-semibold text-blue-500">
											{matchupData.teamOneTitle} <span className="text-lg font-medium text-gray-900">(H)</span>
										</p>

										<div className="flex flex-row gap-2">
											{showHomeTeamWithdrawBadge ? <WithdrawBadge /> : null}
											<span
												className={`text-lg font-medium min-w-6 text-center ${isHomeWinner ? 'text-success-600' : 'text-gray-900'}`}
											>
												{matchupData.teamOneScore}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Mobile Layout */}
					<div className="sm:hidden bg-white rounded-xl ">
						<div className="space-y-2 p-3">
							<div className="flex items-center gap-4">
								<p className="text-sm font-medium text-gray-600">
									{matchupStartDate} {abbreviation}
								</p>
							</div>
							<div className="py-2">{!isMatchupCancelledOrWithdrawn && <MatchupStatusBadge type={matchupData.matchupStatus} />}</div>

							<div className="flex items-center justify-between">
								<p className="text-base font-medium">
									<span className="text-darkorange-400">{matchupData.teamTwoTitle}</span> <span className="text-gray-900">(A)</span>
								</p>

								<div className="flex flex-row gap-2">
									{showAwayTeamWithdrawBadge ? <WithdrawBadge /> : null}
									<span
										className={`text-base font-medium min-w-5 text-center ${isAwayWinner ? 'text-success-600' : 'text-gray-900'}`}
									>
										{matchupData.teamTwoScore}
									</span>
								</div>
							</div>

							<div className="flex items-center justify-between">
								<p className="text-base font-medium">
									<span className="text-darkorange-400">{matchupData.teamOneTitle}</span> <span className="text-gray-900">(H)</span>
								</p>

								<div className="flex flex-row gap-2">
									{showHomeTeamWithdrawBadge ? <WithdrawBadge /> : null}
									<span
										className={`text-base font-medium min-w-5 text-center ${isHomeWinner ? 'text-success-600' : 'text-gray-900'}`}
									>
										{matchupData.teamOneScore}
									</span>
								</div>
							</div>
							<div className="mt-2 flex items-center gap-2">
								<FontAwesomeIcon icon={faLocationDot} className="size-4 text-gray-500" />
								<p className="text-sm font-medium text-gray-600">{matchupData.venue}</p>
							</div>
						</div>
					</div>
				</div>

				{/* Desktop Actions Dropdown */}
				{!isMatchupCancelledOrWithdrawn && !disableActionsDropdown && (
					<div className="hidden min-w-[250px] sm:block print-hidden">
						<Dropdown
							options={MATCH_ACTIONS.map((option) => ({
								value: option.value,
								label: option.label
							}))}
							onSelect={handleActionSelect}
							placeholder={'Withdraw'}
							value={'Withdraw'}
							showAll={false}
							menuListClassName={`min-w-[245px]`}
							className="hover:bg-gray-50"
						/>
						{(matchupData.matchupStatus === MatchupStatusEnum.SCHEDULED_MATCHUP_STATUS ||
							matchupData.matchupStatus === MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS ||
							matchupData.matchupStatus === MatchupStatusEnum.READY_TO_BE_STARTED_STATUS) && (
							<DreamBreakerBox className="min-w-[245px] hidden sm:block mt-2" {...dreamBreakerProps} matchupData={matchupData} />
						)}

						{isTieBreakerMatchCompleted ? (
							<Button
								onClick={scrollToTiebreaker}
								className="float-right font-semibold text-sm bg-darkorange-400 mt-3 text-white rounded-md px-2 py-1"
							>
								Go to {tieBreakerTitle}
							</Button>
						) : null}
					</div>
				)}

				{isMatchupCancelledOrWithdrawn && !disableActionsDropdown ? (
					<Button
						className="hidden sm:flex !bg-orange-500 hover:!bg-orange-700 !text-white text-nowrap"
						onClick={handleUndoAction}
						disabled={isUpdatingMatchup}
					>
						Undo
						{matchupData.matchupCompletedType === MatchupCompletedEnum.HOME_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE ||
						matchupData.matchupCompletedType === MatchupCompletedEnum.AWAY_TEAM_WITHDRAW_MATCHUP_COMPLETION_TYPE
							? ' withdraw'
							: ' cancel'}
					</Button>
				) : null}
			</div>

			{selectedPrintOptions.sheetType === 'simplified' && (
				<SimplifiedScoreSheetPrint
					innerRef={contentRef}
					matches={matchupData.matches}
					teamOneTitle={matchupData.teamOneTitle}
					teamTwoTitle={matchupData.teamTwoTitle}
					sheetSize={selectedPrintOptions.sheetSize === 'half' ? 'Half Sheets' : 'Full Sheets'}
				/>
			)}

			{selectedPrintOptions.sheetType === 'official' && (
				<OfficialScoreSheetPrint
					innerRef={contentRef}
					matches={matchupData.matches}
					sheetSize={selectedPrintOptions.sheetSize === 'half' ? 'Half Sheets' : 'Full Sheets'}
					podsResponse={podsResponse}
					timeZone={matchupData.timezone}
					teamOneTitle={matchupData.teamOneTitle}
					teamTwoTitle={matchupData.teamTwoTitle}
				/>
			)}

			{selectedPrintOptions.sheetType === 'mlp' && (
				<MlpScoreSheetPrint
					innerRef={contentRef}
					matches={matchupData.matches}
					teamOneTitle={matchupData.teamOneTitle}
					teamTwoTitle={matchupData.teamTwoTitle}
					sheetSize={'Half Sheets'} // NOTE: MLP only has half sheets, bcs is the big sheet
					rotations={rotationsInfo?.data ?? { matchUuid: '', teamOne: [], teamTwo: [] }}
				/>
			)}
		</>
	);
};
